import React, { useState, useEffect, RefObject, useLayoutEffect } from "react";
import { translate } from "../contexts/i18n.context";

export const useDevice = () => {
  const [width, setWidth] = useState<number>(0);

  const isMobile: boolean = width <= 768;
  const isMobileTablet: boolean = width <= 1023;
  const isTablet: boolean = width > 769 && width <= 1150;
  const isDesktop: boolean = width > 1150;
  const isTabletLandscape: boolean = width >= 1023 && width <= 1150;

  useEffect(() => {
    if (typeof window === "undefined") return;

    const handleWindowSizeChange = () => setWidth(window.innerWidth);
    handleWindowSizeChange();
    window.addEventListener("resize", handleWindowSizeChange);
    return () => window.removeEventListener("resize", handleWindowSizeChange);
  }, []);

  return {
    isMobile,
    isMobileTablet,
    isTablet,
    isDesktop,
    isTabletLandscape,
  } as const;
};

type TBreadCrumb = {
  name: string;
  link: string;
};

export const getBreadcrumbsFromPath = (pathname: string): Array<TBreadCrumb> => {
  if (!pathname) return [];

  const newBreadCrumbs: Array<TBreadCrumb> = [];
  const parts = pathname.split("/");
  let cumulativeLink = "/";
  for (let index = 0; index < parts.length; index++) {
    const element = parts[index];
    if (element === "") continue;
    const noDash = element.replaceAll("-", " ").replaceAll("_", " ");
    const label = noDash[0].toUpperCase() + noDash.substring(1);
    cumulativeLink = cumulativeLink + element + "/";
    newBreadCrumbs.push({ name: label, link: cumulativeLink });
  }

  let result = newBreadCrumbs.filter(
    (crumb) => crumb.link !== "/en/" && crumb.link !== "/fr/"
  );

  // Blog pagination: collapse trailing "Page" segment into the previous crumb's label.
  const hasPage = result.some((b) => b.name === "Page");
  if (hasPage) {
    result = result.filter((b) => b.name !== "Page");
    if (result.length === 3 && result[2].name) {
      result[2].name = `Page ${result[2].name}`;
    }
    if (result.length === 4 && result[3].name) {
      result[3].name = `Page ${result[3].name}`;
    }
  }

  return result;
};

export const useBreadCrumbs = () => {
  const [breadcrumbs, setBreadcrumbs] = useState<Array<TBreadCrumb>>([]);

  useEffect(() => {
    const url =
      typeof window !== "undefined" ? window.location.pathname : "";
    setBreadcrumbs(getBreadcrumbsFromPath(url));
  }, []);

  return { breadcrumbs };
};

export const buildBreadcrumbSchema = (pathname: string) => {
  if (!pathname || pathname === "/") return null;

  const siteUrl = process.env.GATSBY_SITE_URL || "";
  const parts = pathname.split("/").filter(Boolean);

  // Filter out locale segments
  const filteredParts = parts.filter(p => !["en", "fr", "it", "pt"].includes(p));
  if (filteredParts.length === 0) return null;

  const items = [
    { "@type": "ListItem" as const, "position": 1, "name": "Inicio", "item": `${siteUrl}/` }
  ];

  let cumulativeLink = "/";
  filteredParts.forEach((part, index) => {
    cumulativeLink += part + "/";
    const label = part.replaceAll("-", " ").replaceAll("_", " ");
    const name = label[0].toUpperCase() + label.substring(1);
    items.push({
      "@type": "ListItem" as const,
      "position": index + 2,
      "name": name,
      "item": `${siteUrl}${cumulativeLink}`
    });
  });

  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": items
  };
};

// https://stackoverflow.com/a/65008608
// export function useOnScreen(ref: RefObject<any>) {
//   const [isIntersecting, setIntersecting] = useState(false)
//   const observer = new IntersectionObserver(([entry]) => setIntersecting(entry.isIntersecting))

//   useEffect(() => {
//     observer.observe(ref.current)
//     // Remove the observer as soon as the component is unmounted
//     return () => { observer.disconnect() }
//   }, [])

//   return isIntersecting
// }

// https://usehooks.com/useOnScreen/
export function useOnScreen(ref: RefObject<any>, rootMargin = "0px") {
  // State and setter for storing whether element is visible
  const [isIntersecting, setIntersecting] = useState(false);
  useLayoutEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        // Update our state when observer callback fires
        setIntersecting(entry.isIntersecting);
      },
      {
        rootMargin,
      }
    );
    if (ref.current) {
      observer.observe(ref.current);
    }
    return () => {
      observer.unobserve(ref.current);
    };
  }, []); // Empty array ensures that effect is only run on mount and unmount
  return isIntersecting;
}

const sixHoursDate = new Date(
  new Date().setTime(new Date().getTime() + 6 * 60 * 60 * 1000)
);

export function setCookie(
  name: string,
  _value: string | null,
  _expires: Date = sixHoursDate
) {
  if (typeof document === "undefined") return;
  const value = _value ? encodeURIComponent(_value) : _value;
  const domain = process.env.GATSBY_DOMAIN_URL;
  const expires = _expires.toUTCString();

  document.cookie = `${name}=${value};Domain=${domain};expires=${expires};path=/`;
}

export const getCookie = (name: string, defaultValue: string | null = null) => {
  const isBrowser = typeof window !== "undefined";

  return (
    (isBrowser &&
      document.cookie.split("; ").reduce((r, v): any => {
        const parts = v.split("=");
        if (parts[0] === name) {
          if (parts[1] === "") return "defined";
          if (parts[1] === "null") return null;
          return decodeURIComponent(parts[1]);
        } else {
          return r;
        }
      }, "")) ||
    defaultValue
  );
};

export const getReferrerUrl = () => {
  const referrerUrl = getCookie("referrerUrl");
  if (referrerUrl === "defined") return null;
  return referrerUrl;
};

export const useDragScroll = () => {
  const [node, setNode] = React.useState<HTMLElement>(null);

  const ref = React.useCallback((nodeEle) => {
    setNode(nodeEle);
  }, []);

  const handleMouseDown = React.useCallback(
    (e: React.MouseEvent) => {
      if (!node) {
        return;
      }
      const startPos = {
        left: node.scrollLeft,
        top: node.scrollTop,
        x: e.clientX,
        y: e.clientY,
      };

      const handleMouseMove = (e: React.MouseEvent) => {
        const dx = e.clientX - startPos.x;
        const dy = e.clientY - startPos.y;
        node.scrollTop = startPos.top - dy;
        node.scrollLeft = startPos.left - dx;
        updateCursor(node);
      };

      const handleMouseUp = () => {
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
        resetCursor(node);
      };

      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
    },
    [node]
  );

  const handleTouchStart = React.useCallback(
    (e: React.TouchEvent) => {
      if (!node) {
        return;
      }
      const touch = e.touches[0];
      const startPos = {
        left: node.scrollLeft,
        top: node.scrollTop,
        x: touch.clientX,
        y: touch.clientY,
      };

      const handleTouchMove = (e: React.TouchEvent) => {
        const touch = e.touches[0];
        const dx = touch.clientX - startPos.x;
        const dy = touch.clientY - startPos.y;
        node.scrollTop = startPos.top - dy;
        node.scrollLeft = startPos.left - dx;
        updateCursor(node);
      };

      const handleTouchEnd = () => {
        document.removeEventListener("touchmove", handleTouchMove);
        document.removeEventListener("touchend", handleTouchEnd);
        resetCursor(node);
      };

      document.addEventListener("touchmove", handleTouchMove);
      document.addEventListener("touchend", handleTouchEnd);
    },
    [node]
  );

  const updateCursor = (ele) => {
    ele.style.cursor = "grabbing";
    ele.style.userSelect = "none";
  };

  const resetCursor = (ele) => {
    ele.style.cursor = "grab";
    ele.style.removeProperty("user-select");
  };

  React.useEffect(() => {
    if (!node) {
      return;
    }
    node.addEventListener("mousedown", handleMouseDown);
    node.addEventListener("touchstart", handleTouchStart);
    return () => {
      node.removeEventListener("mousedown", handleMouseDown);
      node.removeEventListener("touchstart", handleTouchStart);
    };
  }, [node]);

  return [ref];
};
